import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emptyLine, linesTotalCents, parseLines, type LineDraft } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from '../create/Field';
import { LineItemBuilder } from './LineItemBuilder';
import { TaxRateField, taxBps } from './TaxRateField';
import { money } from '../money';

/**
 * Billing a job that never had a quote (V5 dogfood — "Invoice this job" used to 409 with
 * "the invoice needs line items" and offer no way to add any, so a job booked straight off
 * a phone call could never be invoiced from the UI at all. That is most jobs).
 *
 * Lines start EMPTY rather than pre-filled from the job's tracked costs. Cost is not price:
 * a sheet that quietly proposed billing the customer what the part cost the shop would turn
 * a convenience into a margin leak nobody notices for months.
 *
 * A T&M job never opens this sheet: it bills part by part through `BillPartsSheet` (Inventory v2
 * ticket 12a).
 */
export function BillJobSheet({
  jobId,
  open,
  onClose,
}: {
  jobId: string;
  open: boolean;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [termsDays, setTermsDays] = useState('30');
  const [taxPct, setTaxPct] = useState('');

  // Same reason as the standalone invoice sheet: a rate box on an exempt customer's bill
  // invites a number the server will throw away.
  // Read the flag by id rather than widening job.get's customer projection — that one is
  // also read by the tech surface, and this screen is office-only. It used to scan the
  // whole customer list, so an exempt customer past the 500-row cap was silently taxed.
  const job = useQuery(trpc.job.get.queryOptions({ jobId }));
  const customerId = job.data?.customer.id;
  const chosen = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: customerId! }),
    enabled: Boolean(customerId),
  });
  const exempt = chosen.data?.customer.taxExempt ?? false;

  const bill = useMutation(
    trpc.invoice.fromJob.mutationOptions({
      onSuccess: (invoice) => {
        void qc.invalidateQueries();
        onClose();
        void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
      },
    }),
  );

  // parseLines is the shared definition of "a row a human actually filled in" — the same
  // one the quote builder uses. Re-deriving it here is how two screens start disagreeing
  // about whether a half-typed row is a line item.
  const usable = parseLines(lines);
  const subtotal = linesTotalCents(lines);

  return (
    <Sheet
      // Sonar-format width: a line-item builder is cramped at 420px (104).
      wide
      open={open}
      onClose={onClose}
      title="Invoice this job"
      footer={
        <Button
          className="w-full"
          disabled={usable.length === 0 || bill.isPending}
          onClick={() =>
            bill.mutate({
              jobId,
              // Blank sends undefined, not 0 — the server falls back to the shop's rate
              // (and to the quote's, if this job came from one).
              taxRateBps: taxBps(taxPct),
              termsDays: Number(termsDays) || 0,
              lines: usable,
              ...idem(),
            })
          }
        >
          {bill.isPending ? 'Creating…' : `Create draft — ${money(subtotal)}`}
        </Button>
      }
    >
      <div className="space-y-3">
        {bill.error ? <Notice tone="danger">{bill.error.message}</Notice> : null}

        <p className="text-sm text-muted-foreground">
          This job didn't come from a quote, so there's nothing to copy — put down what
          you're charging for. It saves as a draft; nothing goes to the customer until you
          issue it.
        </p>

        <LineItemBuilder lines={lines} onChange={setLines} />

        <TaxRateField value={taxPct} onChange={setTaxPct} exempt={exempt} />

        <Field label="Due in (days)">
          <input
            className={field}
            value={termsDays}
            onChange={(e) => setTermsDays(e.target.value)}
            inputMode="numeric"
          />
        </Field>
      </div>
    </Sheet>
  );
}
